import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft, Users, CheckCircle2, XCircle, Search, QrCode,
  Clock, MapPin, BarChart3, Globe, Link as LinkIcon,
  Download, FileSpreadsheet, FileText, Printer, TrendingUp,
  PieChart as PieChartIcon, Award, Ticket, Plus, X, MessageCircle,
  ClipboardList, Image as ImageIcon, Loader2,
  Eye, ExternalLink, Send, AlertTriangle, Mail, UserPlus, Monitor,
  Lock, Unlock, ListOrdered, UserX, Shield, Radio, Video, Wifi
} from "lucide-react";
import { RecapTab } from "./event-detail/RecapTab";
import { StatsTab } from "./event-detail/StatsTab";
import { BadgesTab } from "./event-detail/BadgesTab";
import { TicketsTab } from "./event-detail/TicketsTab";
import { AfficheTab } from "./event-detail/AfficheTab";
import { FormFieldsTab } from "./event-detail/FormFieldsTab";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  registered: "bg-[#0099CC]/20 text-[#0099CC]",
  checked_in: "bg-[#10B981]/20 text-[#10B981]",
  cancelled: "bg-red-500/20 text-red-400",
  waitlisted: "bg-[#F59E0B]/20 text-[#F59E0B]",
};
const STATUS_LABELS = {
  registered: "Inscrit", checked_in: "Present", cancelled: "Annule", waitlisted: "En attente",
};

export default function OrgEventDetailPage() {
  const { token, user: currentUser, isAdmin } = useAuth();
  const { eventId, orgId: routeOrgId } = useParams();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };
  const isSuperAdminMode = isAdmin && !!routeOrgId;

  const [event, setEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [stats, setStats] = useState(null);
  const [advancedStats, setAdvancedStats] = useState(null);
  const [statsPeriod, setStatsPeriod] = useState("all");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("participants");
  const [badgeFormat, setBadgeFormat] = useState("card");
  const [ticketTypes, setTicketTypes] = useState([]);
  const [ticketSaving, setTicketSaving] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [fieldsSaving, setFieldsSaving] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showReminderConfirm, setShowReminderConfirm] = useState(false);
  const [reminderSending, setReminderSending] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [addForm, setAddForm] = useState({ first_name: "", last_name: "", email: "", phone: "", country: "", city: "", is_vip: false, override_capacity: false, override_reason: "" });
  const [addingParticipant, setAddingParticipant] = useState(false);
  const [superAdminOrgName, setSuperAdminOrgName] = useState("");
  const [sortBy, setSortBy] = useState("last_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedParticipants, setSelectedParticipants] = useState(new Set());
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [invitationMessage, setInvitationMessage] = useState("");
  const [invitationSending, setInvitationSending] = useState(false);
  const [invitationResult, setInvitationResult] = useState(null);
  const [replayUrl, setReplayUrl] = useState("");
  const [savingReplay, setSavingReplay] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [evtRes, partRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/event/events/${eventId}`, { headers }),
        axios.get(`${API}/api/event/events/${eventId}/participants?sort_by=${sortBy}&sort_order=${sortOrder}`, { headers }),
        axios.get(`${API}/api/event/events/${eventId}/stats`, { headers }),
      ]);
      setEvent(evtRes.data);
      setReplayUrl(evtRes.data.recording_url || "");
      setParticipants(Array.isArray(partRes.data) ? partRes.data : partRes.data.participants || []);
      setStats(statsRes.data);
      setTicketTypes(evtRes.data.ticket_types || []);
      setCustomFields(evtRes.data.custom_fields || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
      if (e.response?.status === 403) navigate(isSuperAdminMode ? `/app/event-center/org/${routeOrgId}/events` : "/app/evenements");
    } finally { setLoading(false); }
  }, [eventId, token, sortBy, sortOrder]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch org name for super admin banner
  useEffect(() => {
    if (isSuperAdminMode && routeOrgId) {
      axios.get(`${API}/api/event/admin/organizations/${routeOrgId}`, { headers })
        .then(r => setSuperAdminOrgName(r.data.org_name || ""))
        .catch(() => {});
    }
  }, [isSuperAdminMode, routeOrgId]);

  // Load advanced stats when switching to stats tab or changing period
  useEffect(() => {
    if (tab === "stats") {
      axios.get(`${API}/api/event/events/${eventId}/stats/advanced?period=${statsPeriod}`, { headers })
        .then(r => setAdvancedStats(r.data))
        .catch(() => toast.error("Erreur chargement statistiques"));
    }
  }, [tab, statsPeriod]);

  const publicBaseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const copyPublicLink = () => {
    if (!event) return;
    const url = `${publicBaseUrl}/e/${event.org_slug || ""}/${event.slug || ""}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Lien copie !")).catch(() => toast.error("Erreur"));
  };

  const openPreview = async () => {
    if (!event) return;
    if (event.status === "published" || event.status === "ended" || event.status === "cancelled") {
      window.open(`${publicBaseUrl}/e/${event.org_slug || ""}/${event.slug || ""}`, "_blank");
    } else {
      try {
        const res = await axios.post(`${API}/api/event/events/${eventId}/preview-token`, {}, { headers });
        const { preview_token, org_slug, event_slug } = res.data;
        window.open(`${publicBaseUrl}/e/${org_slug}/${event_slug}?preview=${preview_token}`, "_blank");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erreur de previsualisation");
      }
    }
  };

  // ===== PUBLICATION =====
  const togglePublish = async (newStatus) => {
    setPublishing(true);
    try {
      await axios.patch(`${API}/api/event/events/${eventId}`, { status: newStatus }, { headers });
      const updates = { status: newStatus };
      // Auto-close registrations when ending/cancelling
      if (newStatus === "ended" || newStatus === "cancelled") {
        updates.registration_status = "closed";
      }
      setEvent(prev => ({ ...prev, ...updates }));
      setShowUnpublishConfirm(false);
      setShowEndConfirm(false);
      setShowCancelConfirm(false);
      const messages = {
        published: "Evenement publie !",
        draft: "Evenement repasse en brouillon",
        ended: "Evenement marque comme termine",
        cancelled: "Evenement annule",
      };
      toast.success(messages[newStatus] || "Statut mis a jour");
      if (newStatus === "published") {
        toast.success("Evenement publie !", {
          description: "Votre evenement est maintenant accessible au public.",
          action: {
            label: "Voir la page",
            onClick: () => window.open(`${publicBaseUrl}/e/${event.org_slug || ""}/${event.slug || ""}`, "_blank"),
          },
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setPublishing(false); }
  };

  // ===== RAPPEL PARTICIPANTS =====
  const sendReminder = async () => {
    setReminderSending(true);
    setReminderResult(null);
    try {
      const { data } = await axios.post(`${API}/api/event/events/${eventId}/send-reminder`, {}, { headers });
      setReminderResult(data);
      setShowReminderConfirm(false);
      if (data.failed === 0) {
        toast.success(`${data.sent} rappel(s) envoye(s) avec succes`);
      } else {
        toast.warning(`${data.sent} envoye(s), ${data.failed} echec(s)`);
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'envoi");
      setShowReminderConfirm(false);
    } finally { setReminderSending(false); }
  };

  // ===== PERSONALIZED INVITATIONS =====
  const toggleParticipantSelection = (pid) => {
    setSelectedParticipants(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };

  const selectAllParticipants = () => {
    if (selectedParticipants.size === filteredParticipants.length) {
      setSelectedParticipants(new Set());
    } else {
      setSelectedParticipants(new Set(filteredParticipants.map(p => p.id)));
    }
  };

  const sendInvitations = async () => {
    if (selectedParticipants.size === 0) return;
    setInvitationSending(true);
    setInvitationResult(null);
    try {
      const res = await axios.post(`${API}/api/event/events/${eventId}/send-invitations`, {
        participant_ids: Array.from(selectedParticipants),
        custom_message: invitationMessage,
      }, { headers });
      setInvitationResult(res.data);
      if (res.data.sent > 0) toast.success(`${res.data.sent} invitation(s) envoyee(s)`);
      if (res.data.failed > 0) toast.warning(`${res.data.failed} echec(s)`);
      setSelectedParticipants(new Set());
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi");
    } finally { setInvitationSending(false); }
  };

  // ===== CONFERENCE ROLES =====
  const CONF_ROLE_COLORS = { moderator: "#F59E0B", speaker: "#10B981", participant: "#64748b" };
  const CONF_ROLE_LABELS = { moderator: "Moderateur", speaker: "Intervenant", participant: "Participant" };

  const updateConferenceRole = async (participantId, newRole) => {
    try {
      await axios.patch(`${API}/api/event/events/${eventId}/participants/${participantId}/conference-role`, { conference_role: newRole }, { headers });
      setParticipants(ps => ps.map(p => p.id === participantId ? { ...p, conference_role: newRole } : p));
      toast.success(`Role conference: ${CONF_ROLE_LABELS[newRole]}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de mise a jour du role");
    }
  };

  // ===== REPLAY URL =====
  const saveReplayUrl = async () => {
    setSavingReplay(true);
    try {
      await axios.put(`${API}/api/event/events/${eventId}`, { recording_url: replayUrl || null }, { headers });
      setEvent(prev => ({ ...prev, recording_url: replayUrl || null }));
      toast.success(replayUrl ? "URL du replay enregistree" : "Replay supprime");
    } catch (e) {
      toast.error("Erreur de sauvegarde");
    } finally { setSavingReplay(false); }
  };

  // ===== REGISTRATION STATUS =====
  const toggleRegistrationStatus = async (newStatus) => {
    try {
      await axios.patch(`${API}/api/event/events/${eventId}`, { registration_status: newStatus }, { headers });
      setEvent(prev => ({ ...prev, registration_status: newStatus }));
      toast.success(newStatus === "open" ? "Inscriptions ouvertes" : "Inscriptions fermees");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  // ===== WAITLIST =====
  const toggleWaitlist = async (enabled) => {
    try {
      await axios.patch(`${API}/api/event/events/${eventId}`, { waitlist_enabled: enabled }, { headers });
      setEvent(prev => ({ ...prev, waitlist_enabled: enabled }));
      toast.success(enabled ? "Liste d'attente activee" : "Liste d'attente desactivee");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const promoteFromWaitlist = async (participantId) => {
    try {
      await axios.post(`${API}/api/event/events/${eventId}/promote-waitlist/${participantId}`, {}, { headers });
      toast.success("Participant promu !");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur de promotion"); }
  };

  const cancelParticipant = async (participantId) => {
    if (!window.confirm("Confirmer l'annulation de ce participant ?")) return;
    try {
      await axios.post(`${API}/api/event/events/${eventId}/cancel-participant/${participantId}`, {}, { headers });
      toast.success("Participant annule");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  // ===== ADD PARTICIPANT =====
  const addParticipant = async () => {
    if (!addForm.first_name || !addForm.last_name) { toast.error("Nom et prenom obligatoires"); return; }
    setAddingParticipant(true);
    try {
      const res = await axios.post(`${API}/api/event/events/${eventId}/add-participant`, addForm, { headers });
      if (res.data.duplicate_warning) {
        toast.warning(res.data.duplicate_warning, { duration: 6000 });
      }
      toast.success("Participant ajoute !");
      setShowAddParticipant(false);
      setAddForm({ first_name: "", last_name: "", email: "", phone: "", country: "", city: "", is_vip: false, override_capacity: false, override_reason: "" });
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail || "Erreur d'ajout";
      if (err.response?.status === 409) {
        toast.error(detail, { duration: 6000 });
      } else {
        toast.error(detail);
      }
    } finally { setAddingParticipant(false); }
  };

  // ===== PRINT PARTICIPANT LIST =====
  const printParticipantList = () => {
    const listToPrint = [...filtered].sort((a, b) => {
      const la = (a.last_name || "").toLowerCase();
      const lb = (b.last_name || "").toLowerCase();
      if (la !== lb) return la.localeCompare(lb, "fr");
      return (a.first_name || "").toLowerCase().localeCompare((b.first_name || "").toLowerCase(), "fr");
    });
    const eventTitle = event?.title || "Evenement";
    const eventDate = event?.event_date ? new Date(event.event_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "";
    const eventLocation = event?.location || "";
    const count = listToPrint.length;

    const statusLabel = (s) => {
      if (s === "checked_in") return "Present";
      if (s === "registered") return "Inscrit";
      if (s === "cancelled") return "Annule";
      if (s === "waitlisted") return "En attente";
      return s || "-";
    };

    const html = `<!DOCTYPE html>
<html lang="fr"><head><meta charset="utf-8"><title>Liste participants - ${eventTitle}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 24px; color: #1a1a1a; font-size: 11px; }
  .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #0099CC; padding-bottom: 12px; }
  .header h1 { font-size: 18px; color: #0099CC; margin-bottom: 4px; }
  .header .meta { font-size: 11px; color: #555; }
  .header .count { font-size: 13px; font-weight: bold; color: #333; margin-top: 6px; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th { background: #0099CC; color: white; padding: 6px 8px; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
  td { padding: 5px 8px; border-bottom: 1px solid #e0e0e0; font-size: 11px; }
  tr:nth-child(even) { background: #f8f9fa; }
  tr:hover { background: #e8f4fd; }
  .footer { text-align: center; margin-top: 16px; font-size: 9px; color: #999; border-top: 1px solid #ddd; padding-top: 8px; }
  .status-present { color: #10B981; font-weight: bold; }
  .status-inscrit { color: #0099CC; }
  .status-annule { color: #EF4444; text-decoration: line-through; }
  .status-attente { color: #F59E0B; }
  @media print { body { padding: 12px; } .no-print { display: none; } }
</style></head><body>
<div class="header">
  <h1>${eventTitle}</h1>
  <div class="meta">${eventDate}${eventLocation ? " — " + eventLocation : ""}</div>
  <div class="count">${count} participant${count > 1 ? "s" : ""}${search ? " (filtre actif)" : ""}</div>
</div>
<table>
  <thead><tr><th>N°</th><th>Nom</th><th>Prenom</th><th>Telephone</th><th>E-mail</th><th>Categorie</th><th>Statut</th></tr></thead>
  <tbody>
    ${listToPrint.map((p, i) => `<tr>
      <td>${i + 1}</td>
      <td style="font-weight:600">${p.last_name || "-"}</td>
      <td>${p.first_name || "-"}</td>
      <td>${p.phone || "-"}</td>
      <td>${p.email || "-"}</td>
      <td>${p.category || "Standard"}</td>
      <td class="status-${statusLabel(p.status).toLowerCase().replace(/ /g,"-")}">${statusLabel(p.status)}</td>
    </tr>`).join("")}
  </tbody>
</table>
<div class="footer">Imprime le ${new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })} — GLOTECH Event Center</div>
<script>window.onload=function(){window.print();}</script>
</body></html>`;

    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); }
  };

  // ===== EXPORTS =====
  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/api/event/events/${eventId}/export?format=csv`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `participants_${event?.title || "export"}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export CSV telecharge");
    } catch { toast.error("Erreur d'export"); }
  };

  const exportExcel = async () => {
    try {
      const { data } = await axios.get(`${API}/api/event/events/${eventId}/export?format=json`, { headers });
      const XLSX = await import("xlsx");
      const rows = (data.participants || []).map(p => ({
        "Reference": p.reference, "Prenom": p.first_name, "Nom": p.last_name,
        "Email": p.email, "Telephone": p.phone, "Categorie": p.category || "Standard",
        "Statut": p.status === "checked_in" ? "Present" : p.status === "registered" ? "Inscrit" : p.status,
        "VIP": p.is_vip ? "Oui" : "Non", "Date": p.created_at,
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Participants");
      XLSX.writeFile(wb, `participants_${event?.title || "export"}.xlsx`);
      toast.success("Export Excel telecharge");
    } catch { toast.error("Erreur d'export"); }
  };

  const exportPDF = async () => {
    try {
      const { data } = await axios.get(`${API}/api/event/events/${eventId}/export?format=json`, { headers });
      const jsPDF = (await import("jspdf")).default;
      const autoTable = (await import("jspdf-autotable")).default;
      const doc = new jsPDF();
      doc.setFontSize(16); doc.text(event?.title || "Participants", 14, 20);
      doc.setFontSize(10); doc.text(`Total: ${data.participants?.length || 0} participants`, 14, 28);
      autoTable(doc, {
        startY: 35,
        head: [["Ref", "Prenom", "Nom", "Email", "Categorie", "Statut"]],
        body: (data.participants || []).map(p => [
          p.reference, p.first_name, p.last_name, p.email || "",
          p.category || "Standard",
          p.status === "checked_in" ? "Present" : p.status === "registered" ? "Inscrit" : p.status,
        ]),
        styles: { fontSize: 8 }, headStyles: { fillColor: [0, 153, 204] },
      });
      doc.save(`participants_${event?.title || "export"}.pdf`);
      toast.success("Export PDF telecharge");
    } catch (e) { console.error(e); toast.error("Erreur d'export PDF"); }
  };

  // ===== BADGES =====
  const generateBadges = async (participantsList, format) => {
    try {
      const jsPDF = (await import("jspdf")).default;
      const QRCode = (await import("qrcode")).default;

      if (format === "card") {
        // A6 format — 1 badge per page (105x148mm)
        const doc = new jsPDF({ format: [105, 148], unit: "mm" });
        for (let i = 0; i < participantsList.length; i++) {
          const p = participantsList[i];
          if (i > 0) doc.addPage([105, 148]);

          // Background
          doc.setFillColor(11, 15, 25); doc.rect(0, 0, 105, 148, "F");
          // Header bar
          doc.setFillColor(0, 153, 204); doc.rect(0, 0, 105, 28, "F");
          doc.setTextColor(255, 255, 255); doc.setFontSize(11); doc.setFont(undefined, "bold");
          doc.text(event?.title || "Evenement", 52.5, 12, { align: "center" });
          doc.setFontSize(7); doc.setFont(undefined, "normal");
          if (event?.location) doc.text(event.location, 52.5, 19, { align: "center" });
          if (event?.start_date) {
            const d = new Date(event.start_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
            doc.text(d, 52.5, 24, { align: "center" });
          }

          // Category badge
          if (p.is_vip || p.category === "vip") {
            doc.setFillColor(245, 158, 11); doc.roundedRect(35, 32, 35, 7, 2, 2, "F");
            doc.setTextColor(0, 0, 0); doc.setFontSize(8); doc.setFont(undefined, "bold");
            doc.text("VIP", 52.5, 37, { align: "center" });
          } else {
            doc.setFillColor(42, 52, 65); doc.roundedRect(30, 32, 45, 7, 2, 2, "F");
            doc.setTextColor(148, 163, 184); doc.setFontSize(8); doc.setFont(undefined, "bold");
            doc.text((p.category || "Standard").toUpperCase(), 52.5, 37, { align: "center" });
          }

          // Name
          doc.setTextColor(255, 255, 255); doc.setFontSize(16); doc.setFont(undefined, "bold");
          doc.text(p.first_name || "", 52.5, 54, { align: "center" });
          doc.setFontSize(14); doc.setFont(undefined, "normal");
          doc.text((p.last_name || "").toUpperCase(), 52.5, 63, { align: "center" });

          // Organization
          if (p.organization) {
            doc.setTextColor(148, 163, 184); doc.setFontSize(8);
            doc.text(p.organization, 52.5, 72, { align: "center" });
          }

          // QR Code
          const qr_data = `${eventId}:${p.qr_token || p.reference}`;
          const qrUrl = await QRCode.toDataURL(qr_data, { width: 200, margin: 1, color: { dark: "#0099CC", light: "#0B0F19" } });
          doc.addImage(qrUrl, "PNG", 32.5, 78, 40, 40);

          // Reference
          doc.setTextColor(100, 116, 139); doc.setFontSize(7);
          doc.text(`REF: ${p.reference || "—"}`, 52.5, 124, { align: "center" });

          // Footer
          doc.setFillColor(0, 153, 204); doc.rect(0, 132, 105, 16, "F");
          doc.setTextColor(255, 255, 255); doc.setFontSize(6);
          doc.text("Scannez le QR code a l'entree pour valider votre acces", 52.5, 140, { align: "center" });
        }
        doc.save(`badges_${event?.title || "event"}_carte.pdf`);
      } else {
        // Grid format — 6 badges per A4 page (3 rows x 2 cols)
        const doc = new jsPDF({ format: "a4", unit: "mm" });
        const bw = 90, bh = 85, mx = 10, my = 10, gx = 5, gy = 5;

        for (let i = 0; i < participantsList.length; i++) {
          const page = Math.floor(i / 6);
          const pos = i % 6;
          if (i > 0 && pos === 0) doc.addPage();
          const col = pos % 2, row = Math.floor(pos / 2);
          const x = mx + col * (bw + gx), y = my + row * (bh + gy);
          const p = participantsList[i];

          // Card background
          doc.setDrawColor(42, 52, 65); doc.setFillColor(21, 27, 43);
          doc.roundedRect(x, y, bw, bh, 3, 3, "FD");

          // Header
          doc.setFillColor(0, 153, 204); doc.rect(x, y, bw, 14, "F");
          doc.setTextColor(255, 255, 255); doc.setFontSize(7); doc.setFont(undefined, "bold");
          doc.text(event?.title || "", x + bw / 2, y + 6, { align: "center" });
          doc.setFontSize(5); doc.setFont(undefined, "normal");
          if (event?.start_date) {
            const d = new Date(event.start_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
            doc.text(`${event?.location || ""} — ${d}`, x + bw / 2, y + 11, { align: "center" });
          }

          // Category
          if (p.is_vip || p.category === "vip") {
            doc.setFillColor(245, 158, 11); doc.roundedRect(x + 30, y + 17, 30, 5, 1.5, 1.5, "F");
            doc.setTextColor(0, 0, 0); doc.setFontSize(6); doc.setFont(undefined, "bold");
            doc.text("VIP", x + bw / 2, y + 20.5, { align: "center" });
          }

          // Name
          doc.setTextColor(255, 255, 255); doc.setFontSize(11); doc.setFont(undefined, "bold");
          doc.text(p.first_name || "", x + bw / 2, y + 30, { align: "center" });
          doc.setFontSize(10); doc.setFont(undefined, "normal");
          doc.text((p.last_name || "").toUpperCase(), x + bw / 2, y + 37, { align: "center" });

          // QR Code
          const qr_data = `${eventId}:${p.qr_token || p.reference}`;
          const qrUrl = await QRCode.toDataURL(qr_data, { width: 150, margin: 1, color: { dark: "#0099CC", light: "#151B2B" } });
          doc.addImage(qrUrl, "PNG", x + 33, y + 42, 24, 24);

          // Reference
          doc.setTextColor(100, 116, 139); doc.setFontSize(5);
          doc.text(`REF: ${p.reference || "—"}`, x + bw / 2, y + 71, { align: "center" });

          // Cut line (dashed)
          doc.setLineDashPattern([1, 1], 0); doc.setDrawColor(60, 70, 90);
          doc.rect(x, y, bw, bh);
          doc.setLineDashPattern([], 0);
        }
        doc.save(`badges_${event?.title || "event"}_grille.pdf`);
      }
      toast.success(`${participantsList.length} badge(s) genere(s) en format ${format === "card" ? "carte" : "grille"}`);
    } catch (e) { console.error(e); toast.error("Erreur de generation des badges"); }
  };

  const filtered = participants.filter(p =>
    `${p.first_name} ${p.last_name} ${p.email} ${p.reference}`.toLowerCase().includes(search.toLowerCase())
  );
  const filteredParticipants = filtered;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0099CC]" />
    </div>
  );

  if (!event) return (
    <div className="text-center py-12">
      <p className="text-[#64748b]">Evenement introuvable</p>
      <Button onClick={() => navigate(isSuperAdminMode ? `/app/event-center/org/${routeOrgId}/events` : "/app/evenements")} variant="outline" className="mt-4">Retour</Button>
    </div>
  );

  const TABS = [
    { id: "participants", label: "Participants", icon: Users },
    { id: "stats", label: "Statistiques", icon: TrendingUp },
    { id: "recap", label: "Recapitulatif", icon: FileText },
    { id: "affiche", label: "Affiche", icon: ImageIcon },
    { id: "badges", label: "Badges", icon: Award },
    { id: "tickets", label: "Billetterie", icon: Ticket },
    { id: "form", label: "Formulaire", icon: ClipboardList },
  ];

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["jpg","jpeg","png","webp"].includes(ext)) { toast.error("Format non supporte (jpg, png, webp)"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image trop volumineuse (max 10 Mo)"); return; }
    setCoverUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/api/event/events/${eventId}/cover`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      setEvent(prev => ({ ...prev, cover_image_url: res.data.cover_image_url }));
      toast.success("Affiche telechargee !");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setCoverUploading(false); }
  };

  const handleCoverDelete = async () => {
    try {
      await axios.delete(`${API}/api/event/events/${eventId}/cover`, { headers });
      setEvent(prev => ({ ...prev, cover_image_url: null }));
      toast.success("Affiche supprimee");
    } catch { toast.error("Erreur"); }
  };

  const addTicketType = () => {
    setTicketTypes(t => [...t, { id: crypto.randomUUID(), name: "", price: 0, currency: "EUR", quantity: 100, sold: 0, description: "" }]);
  };
  const removeTicketType = (id) => setTicketTypes(t => t.filter(x => x.id !== id));
  const updateTicketType = (id, field, value) => {
    setTicketTypes(t => t.map(x => x.id === id ? { ...x, [field]: value } : x));
  };
  const saveTicketTypes = async () => {
    setTicketSaving(true);
    try {
      await axios.put(`${API}/api/event/events/${eventId}/tickets`, { ticket_types: ticketTypes }, { headers });
      toast.success("Tarifs enregistres");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setTicketSaving(false); }
  };

  const shareWhatsApp = () => {
    if (!event) return;
    const url = `${publicBaseUrl}/e/${event.org_slug || ""}/${event.slug || ""}`;
    const date = event.event_date ? new Date(event.event_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "";
    const text = encodeURIComponent(`Vous etes invite(e) a ${event.title}\n${date}${event.location ? `\n${event.location}` : ""}\n\nInscription : ${url}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const addField = () => setCustomFields(f => [...f, { id: crypto.randomUUID(), label: "", type: "text", required: false, placeholder: "", options: [], order: f.length }]);
  const removeField = (id) => setCustomFields(f => f.filter(x => x.id !== id));
  const updateField = (id, key, val) => setCustomFields(f => f.map(x => x.id === id ? { ...x, [key]: val } : x));
  const moveField = (idx, dir) => {
    setCustomFields(f => {
      const arr = [...f]; const ni = idx + dir;
      if (ni < 0 || ni >= arr.length) return f;
      [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
      return arr.map((x, i) => ({ ...x, order: i }));
    });
  };
  const saveFields = async () => {
    setFieldsSaving(true);
    try {
      await axios.put(`${API}/api/event/events/${eventId}/form`, { fields: customFields }, { headers });
      toast.success("Formulaire enregistre");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setFieldsSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="org-event-detail">
      {/* Super Admin Banner */}
      {isSuperAdminMode && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center justify-between" data-testid="super-admin-event-banner">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-amber-400" />
            <span className="text-amber-400 font-bold text-sm">MODE SUPER ADMIN</span>
            <span className="text-amber-200/70 text-sm">— Vous administrez actuellement</span>
            <span className="text-white font-semibold text-sm">{superAdminOrgName || "..."}</span>
          </div>
          <button
            onClick={() => navigate("/app/event-center")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-xs font-medium transition-all"
            data-testid="exit-super-admin-from-event"
          >
            <X className="w-3.5 h-3.5" /> Quitter le mode Super Admin
          </button>
        </div>
      )}

      {/* Back + Header */}
      <div>
        <button onClick={() => navigate(isSuperAdminMode ? `/app/event-center/org/${routeOrgId}/events` : "/app/evenements")} className="flex items-center gap-1 text-sm text-[#64748b] hover:text-white mb-4 transition-colors" data-testid="back-to-events-btn">
          <ArrowLeft className="h-4 w-4" /> {isSuperAdminMode ? `Retour aux evenements de ${superAdminOrgName}` : "Retour aux evenements"}
        </button>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white">{event.title}</h1>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                event.status === "published" ? "bg-[#10B981]/20 text-[#10B981]" :
                event.status === "ended" ? "bg-[#8B5CF6]/20 text-[#8B5CF6]" :
                event.status === "cancelled" ? "bg-red-500/20 text-red-400" :
                "bg-[#64748b]/20 text-[#94A3B8]"
              }`} data-testid="event-status-badge">
                {event.status === "published" ? "Publie" : event.status === "ended" ? "Termine" : event.status === "cancelled" ? "Annule" : "Brouillon"}
              </span>
              {event.event_format && event.event_format !== "presentiel" && (
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${
                  event.event_format === "hybride" ? "bg-[#8B5CF6]/10 text-[#8B5CF6]" : "bg-[#3B82F6]/10 text-[#3B82F6]"
                }`} data-testid="event-detail-format-badge">
                  {event.event_format === "hybride" ? <Globe className="h-2.5 w-2.5" /> : <Wifi className="h-2.5 w-2.5" />}
                  {event.event_format === "hybride" ? "Hybride" : "En ligne"}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-[#64748b] mt-1">
              {event.start_date && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{new Date(event.start_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>}
              {event.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{event.location}</span>}
              {event.meeting_config?.provider && (
                <span className="flex items-center gap-1 text-[#3B82F6]" data-testid="event-meeting-provider">
                  <Video className="h-3.5 w-3.5" />
                  {event.meeting_config.provider === "zoom" ? "Zoom" : event.meeting_config.provider === "google_meet" ? "Google Meet" : event.meeting_config.provider === "glotech_live" ? "GLOTECH Live" : "Lien externe"}
                </span>
              )}
              {event.event_format && event.event_format !== "presentiel" && (
                <>
                  <button onClick={() => navigate(`/app/live-meeting/${event.id}`)}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25 border border-[#10B981]/30 transition-all"
                    data-testid="open-live-meeting-btn">
                    <Video className="h-3.5 w-3.5" /> Rejoindre le Live
                  </button>
                  <button onClick={() => navigate(`/app/control-center/${event.id}`)}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#0099CC]/15 text-[#0099CC] hover:bg-[#0099CC]/25 border border-[#0099CC]/30 transition-all"
                    data-testid="open-control-center-btn">
                    <Monitor className="h-3.5 w-3.5" /> Centre de Controle
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Replay section for online/hybrid events */}
          {(event.event_format === "en_ligne" || event.event_format === "hybride") && (
            <div className="mt-4 bg-[#0B1120] border border-[#1E3A5F] rounded-xl p-4" data-testid="replay-section">
              <div className="flex items-center gap-2 mb-3">
                <Video className="h-4 w-4 text-[#8B5CF6]" />
                <span className="text-sm font-semibold text-[#8B5CF6]">Replay de l'evenement</span>
                {event.recording_url && <span className="text-[10px] bg-[#10B981]/20 text-[#10B981] px-1.5 py-0.5 rounded-full">Disponible</span>}
              </div>
              <div className="flex gap-2">
                <Input data-testid="replay-url-input" value={replayUrl} onChange={e => setReplayUrl(e.target.value)}
                  placeholder="https://youtube.com/watch?v=... ou lien du replay"
                  className="bg-[#151B2B] border-[#2A3441] text-white text-sm flex-1" />
                <Button data-testid="save-replay-btn" onClick={saveReplayUrl} disabled={savingReplay}
                  className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-sm px-4" size="sm">
                  {savingReplay ? "..." : "Enregistrer"}
                </Button>
              </div>
              <p className="text-[10px] text-[#64748b] mt-2">Ce lien sera visible par les participants inscrits sur la page de l'evenement.</p>
            </div>
          )}
          <div className="flex gap-2 flex-wrap">
            {/* Draft: show Publish */}
            {event.status === "draft" && (
              <Button data-testid="publish-event-btn" onClick={() => togglePublish("published")} disabled={publishing} className="bg-[#10B981] hover:bg-[#059669] text-white text-xs">
                {publishing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Globe className="h-3.5 w-3.5 mr-1.5" />}
                Publier
              </Button>
            )}
            {/* Published: show Brouillon, Terminer, Annuler */}
            {event.status === "published" && (
              <>
                <Button data-testid="unpublish-event-btn" variant="outline" onClick={() => setShowUnpublishConfirm(true)} disabled={publishing} className="border-[#F59E0B]/30 text-[#F59E0B] hover:bg-[#F59E0B]/10 text-xs">
                  <AlertTriangle className="h-3.5 w-3.5 mr-1.5" /> Brouillon
                </Button>
                <Button data-testid="end-event-btn" variant="outline" onClick={() => setShowEndConfirm(true)} disabled={publishing} className="border-[#8B5CF6]/30 text-[#8B5CF6] hover:bg-[#8B5CF6]/10 text-xs">
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Terminer
                </Button>
                <Button data-testid="cancel-event-btn" variant="outline" onClick={() => setShowCancelConfirm(true)} disabled={publishing} className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs">
                  <XCircle className="h-3.5 w-3.5 mr-1.5" /> Annuler
                </Button>
              </>
            )}
            {/* Ended / Cancelled: show Republier */}
            {(event.status === "ended" || event.status === "cancelled") && (
              <Button data-testid="republish-event-btn" onClick={() => togglePublish("published")} disabled={publishing} className="bg-[#10B981] hover:bg-[#059669] text-white text-xs">
                {publishing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Globe className="h-3.5 w-3.5 mr-1.5" />}
                Republier
              </Button>
            )}
            <Button data-testid="preview-event-btn" variant="outline" onClick={openPreview} className="border-[#2A3441] text-[#94A3B8] hover:text-white text-xs">
              {event.status === "published" || event.status === "ended" || event.status === "cancelled" ? (
                <><ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Voir la page publique</>
              ) : (
                <><Eye className="h-3.5 w-3.5 mr-1.5" /> Previsualiser</>
              )}
            </Button>
            <Button data-testid="copy-public-link-btn" variant="outline" onClick={copyPublicLink} className="border-[#2A3441] text-[#94A3B8] hover:text-white text-xs">
              <LinkIcon className="h-3.5 w-3.5 mr-1.5" /> Lien public
            </Button>
            <Button data-testid="share-whatsapp-btn" onClick={shareWhatsApp} className="bg-[#25D366] hover:bg-[#20BD5C] text-white text-xs">
              <MessageCircle className="h-3.5 w-3.5 mr-1.5" /> WhatsApp
            </Button>
            <Button onClick={() => navigate(isSuperAdminMode ? `/app/event-center/org/${routeOrgId}/scanner` : "/app/scanner")} className="bg-[#10B981] hover:bg-[#059669] text-white text-xs">
              <QrCode className="h-3.5 w-3.5 mr-1.5" /> Scanner
            </Button>
            <Button onClick={() => navigate(isSuperAdminMode ? `/app/event-center/org/${routeOrgId}/event/${eventId}/live` : `/app/evenements/${eventId}/live`)} className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-xs" data-testid="live-screen-btn">
              <Monitor className="h-3.5 w-3.5 mr-1.5" /> Live
            </Button>
            <Button onClick={() => navigate(isSuperAdminMode ? `/app/event-center/org/${routeOrgId}/event/${eventId}/live-control` : `/app/evenements/${eventId}/live-control`)} className="bg-red-600 hover:bg-red-700 text-white text-xs" data-testid="live-control-btn">
              <Radio className="h-3.5 w-3.5 mr-1.5" /> Regie Live
            </Button>
          </div>
          {/* Registration status control */}
          <div className="flex items-center gap-2 mt-2">
            {(event.registration_status || "open") === "open" ? (
              <button data-testid="close-registrations-btn" onClick={() => toggleRegistrationStatus("closed")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981] hover:bg-[#10B981]/20 text-xs font-medium transition-all">
                <Unlock className="h-3 w-3" /> Inscriptions ouvertes
              </button>
            ) : (
              <button data-testid="open-registrations-btn" onClick={() => toggleRegistrationStatus("open")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 text-xs font-medium transition-all">
                <Lock className="h-3 w-3" /> Inscriptions fermees — Cliquer pour rouvrir
              </button>
            )}
            <button onClick={() => toggleWaitlist(!event.waitlist_enabled)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${event.waitlist_enabled ? "bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-[#F59E0B]" : "bg-[#151B2B] border border-[#2A3441] text-[#64748b] hover:text-white"}`}
              data-testid="waitlist-toggle-btn">
              <ListOrdered className="h-3 w-3" /> {event.waitlist_enabled ? "Liste d'attente ON" : "Liste d'attente OFF"}
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { icon: Users, color: "#0099CC", value: stats.registered, label: "Inscrits" },
            { icon: CheckCircle2, color: "#10B981", value: stats.checked_in, label: "Presents" },
            { icon: ListOrdered, color: "#F59E0B", value: event.total_waitlisted || 0, label: "En attente" },
            { icon: XCircle, color: "#EF4444", value: event.total_cancelled || 0, label: "Annules" },
            { icon: BarChart3, color: "#8B5CF6", value: `${stats.attendance_rate?.toFixed(0) || 0}%`, label: "Taux presence" },
          ].map(s => (
            <div key={s.label} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-4">
              <s.icon className="h-5 w-5 mb-2" style={{ color: s.color }} />
              <p className="text-2xl font-bold text-white">{s.value}</p>
              <p className="text-xs text-[#64748b]">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-[#0B0F19] border border-[#1E293B] rounded-xl p-1">
        {TABS.map(t => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all flex-1 justify-center ${tab === t.id ? "bg-[#0099CC] text-white" : "text-[#64748b] hover:text-white hover:bg-[#151B2B]"}`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* ===== TAB: PARTICIPANTS ===== */}
      {tab === "participants" && (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4A5568]" />
            <Input data-testid="participant-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher un participant (nom, email, reference)..."
              className="pl-10 bg-[#0B0F19] border-[#2A3441] text-white" />
          </div>

          <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-[#1E293B]">
              <h2 className="font-bold text-white flex items-center gap-2">
                <Users className="h-4 w-4 text-[#0099CC]" /> Participants ({filtered.length})
              </h2>
              {participants.length > 0 && (
                <div className="flex gap-1.5 flex-wrap">
                  {selectedParticipants.size > 0 && (
                    <button data-testid="send-invitation-btn" onClick={() => setShowInvitationModal(true)}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981] hover:text-white hover:border-[#10B981]/50 text-xs transition-all font-medium">
                      <Send className="h-3 w-3" /> Inviter ({selectedParticipants.size})
                    </button>
                  )}
                  <button data-testid="add-participant-btn" onClick={() => setShowAddParticipant(true)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#0099CC]/10 border border-[#0099CC]/30 text-[#0099CC] hover:text-white hover:border-[#0099CC]/50 text-xs transition-all"><UserPlus className="h-3 w-3" /> Ajouter</button>
                  <button data-testid="send-reminder-btn" onClick={() => setShowReminderConfirm(true)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#151B2B] border border-[#2A3441] text-[#F59E0B] hover:text-white hover:border-[#F59E0B]/50 text-xs transition-all"><Mail className="h-3 w-3" /> Rappel</button>
                  <button data-testid="print-list-btn" onClick={printParticipantList} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#151B2B] border border-[#2A3441] text-[#8B5CF6] hover:text-white hover:border-[#8B5CF6]/50 text-xs transition-all"><Printer className="h-3 w-3" /> Imprimer</button>
                  <button data-testid="export-csv-btn" onClick={exportCSV} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#151B2B] border border-[#2A3441] text-[#94A3B8] hover:text-white hover:border-[#0099CC]/50 text-xs transition-all"><Download className="h-3 w-3" /> CSV</button>
                  <button data-testid="export-excel-btn" onClick={exportExcel} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#151B2B] border border-[#2A3441] text-[#94A3B8] hover:text-white hover:border-[#10B981]/50 text-xs transition-all"><FileSpreadsheet className="h-3 w-3" /> Excel</button>
                  <button data-testid="export-pdf-btn" onClick={exportPDF} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#151B2B] border border-[#2A3441] text-[#94A3B8] hover:text-white hover:border-red-500/50 text-xs transition-all"><FileText className="h-3 w-3" /> PDF</button>
                </div>
              )}
            </div>
            {/* Sort controls */}
            {filtered.length > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 border-b border-[#1E293B] bg-[#080C14]">
                <span className="text-[10px] text-[#64748B] uppercase tracking-wider">Trier par</span>
                {[
                  { key: "last_name", label: "Nom" },
                  { key: "first_name", label: "Prenom" },
                  { key: "created_at", label: "Date" },
                  { key: "email", label: "Email" },
                ].map(s => (
                  <button key={s.key} onClick={() => { if (sortBy === s.key) setSortOrder(sortOrder === "asc" ? "desc" : "asc"); else { setSortBy(s.key); setSortOrder("asc"); } }}
                    className={`px-2 py-0.5 rounded text-[10px] font-medium transition-all ${sortBy === s.key ? "bg-[#0099CC]/20 text-[#0099CC] border border-[#0099CC]/30" : "text-[#64748B] hover:text-white"}`}
                    data-testid={`sort-${s.key}`}>
                    {s.label} {sortBy === s.key && (sortOrder === "asc" ? "↑" : "↓")}
                  </button>
                ))}
              </div>
            )}
            {filtered.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="h-10 w-10 text-[#2A3441] mx-auto mb-3" />
                <p className="text-[#64748b]">{search ? "Aucun participant trouve" : "Aucun inscrit pour le moment"}</p>
                {event.status !== "published" && !search && <p className="text-[#4A5568] text-sm mt-2">Publiez votre evenement pour recevoir des inscriptions.</p>}
                <button data-testid="add-participant-empty-btn" onClick={() => setShowAddParticipant(true)} className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#0099CC]/10 border border-[#0099CC]/30 text-[#0099CC] hover:text-white text-xs transition-all">
                  <UserPlus className="h-3 w-3" /> Ajouter manuellement un participant
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#1E293B] text-[#64748b] text-xs uppercase tracking-wider">
                      <th className="p-3 pl-4 w-8">
                        <input type="checkbox" data-testid="select-all-checkbox"
                          checked={selectedParticipants.size === filteredParticipants.length && filteredParticipants.length > 0}
                          onChange={selectAllParticipants}
                          className="rounded border-[#2A3441]" />
                      </th>
                      <th className="text-left p-3">Nom</th>
                      <th className="text-left p-3 hidden sm:table-cell">Email</th>
                      <th className="text-left p-3 hidden md:table-cell">Reference</th>
                      <th className="text-left p-3 hidden md:table-cell">Categorie</th>
                      <th className="text-left p-3">Statut</th>
                      <th className="text-left p-3 hidden md:table-cell">Role conf.</th>
                      <th className="text-left p-3 hidden lg:table-cell">Inscription</th>
                      <th className="text-left p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(p => (
                      <tr key={p.id} data-testid="participant-row" className={`border-b border-[#1E293B]/50 hover:bg-[#0F1420] transition-colors ${selectedParticipants.has(p.id) ? "bg-[#0099CC]/5" : ""}`}>
                        <td className="p-3 pl-4 w-8">
                          <input type="checkbox" data-testid={`select-participant-${p.id}`}
                            checked={selectedParticipants.has(p.id)}
                            onChange={() => toggleParticipantSelection(p.id)}
                            className="rounded border-[#2A3441]" />
                        </td>
                        <td className="p-3">
                          <div className="font-medium text-white">{p.first_name} {p.last_name}{p.is_vip && <span className="ml-1.5 text-[10px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full">VIP</span>}</div>
                          <div className="text-xs text-[#64748b] sm:hidden">{p.email}</div>
                        </td>
                        <td className="p-3 text-[#94A3B8] hidden sm:table-cell">{p.email}</td>
                        <td className="p-3 hidden md:table-cell"><span className="font-mono text-xs text-[#0099CC] bg-[#0099CC]/10 px-2 py-0.5 rounded">{p.reference}</span></td>
                        <td className="p-3 hidden md:table-cell">
                          {p.category === "vip" || p.category === "VIP" ? <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">VIP</span>
                           : <span className="text-xs text-[#64748b]">{p.category || "Standard"}</span>}
                        </td>
                        <td className="p-3"><span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[p.status] || ""}`}>{STATUS_LABELS[p.status] || p.status}</span></td>
                        <td className="p-3 hidden md:table-cell">
                          <select
                            value={p.conference_role || "participant"}
                            onChange={e => updateConferenceRole(p.id, e.target.value)}
                            className="bg-[#0B0F19] border border-[#2A3441] rounded-lg px-2 py-1 text-[10px] font-semibold cursor-pointer hover:border-[#0099CC]/50 transition-colors"
                            style={{ color: CONF_ROLE_COLORS[p.conference_role || "participant"] }}
                            data-testid={`conf-role-${p.id}`}
                          >
                            <option value="participant">Participant</option>
                            <option value="speaker">Intervenant</option>
                            <option value="moderator">Moderateur</option>
                          </select>
                        </td>
                        <td className="p-3 text-[#64748b] text-xs hidden lg:table-cell">{p.registered_at || p.created_at ? new Date(p.registered_at || p.created_at).toLocaleDateString("fr-FR") : "—"}</td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            {p.status === "waitlisted" && (
                              <button onClick={() => promoteFromWaitlist(p.id)} className="text-[10px] px-2 py-0.5 rounded bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/20" data-testid="promote-btn">Confirmer</button>
                            )}
                            {(p.status === "registered" || p.status === "waitlisted") && (
                              <button onClick={() => cancelParticipant(p.id)} className="text-[10px] px-2 py-0.5 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20" data-testid="cancel-participant-btn"><UserX className="h-3 w-3" /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ===== TAB: STATISTIQUES ===== */}
      {tab === "stats" && (
        <div className="space-y-6" data-testid="stats-tab">
          <StatsTab advancedStats={advancedStats} statsPeriod={statsPeriod} setStatsPeriod={setStatsPeriod} />
        </div>
      )}

      {/* ===== TAB: RECAP ===== */}
      {tab === "recap" && (
        <RecapTab eventId={eventId} headers={headers} />
      )}

      {/* ===== TAB: BADGES ===== */}
      {tab === "badges" && (
        <BadgesTab participants={participants} filtered={filtered} badgeFormat={badgeFormat} setBadgeFormat={setBadgeFormat} generateBadges={generateBadges} />
      )}

      {/* ===== TAB: BILLETTERIE ===== */}
      {tab === "tickets" && (
        <TicketsTab ticketTypes={ticketTypes} addTicketType={addTicketType} removeTicketType={removeTicketType} updateTicketType={updateTicketType} ticketSaving={ticketSaving} saveTicketTypes={saveTicketTypes} />
      )}
      {/* ===== TAB: AFFICHE ===== */}
      {tab === "affiche" && (
        <AfficheTab event={event} setEvent={setEvent} headers={headers} coverUploading={coverUploading} handleCoverUpload={handleCoverUpload} handleCoverDelete={handleCoverDelete} />
      )}
      {/* ===== TAB: FORMULAIRE ===== */}
      {tab === "form" && (
        <FormFieldsTab customFields={customFields} addField={addField} removeField={removeField} updateField={updateField} moveField={moveField} fieldsSaving={fieldsSaving} saveFields={saveFields} />
      )}

      {/* ===== MODAL: UNPUBLISH CONFIRMATION ===== */}
      {showUnpublishConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="unpublish-modal">
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-6 max-w-md w-full space-y-4">
            <div className="flex items-center gap-3">
              <div className="bg-[#F59E0B]/10 p-2 rounded-full"><AlertTriangle className="h-5 w-5 text-[#F59E0B]" /></div>
              <h3 className="text-white font-bold text-lg">Repasser en brouillon ?</h3>
            </div>
            <p className="text-[#94A3B8] text-sm">
              Cet evenement est actuellement publie. Son lien public a peut-etre deja ete partage. En le repassant en brouillon, il ne sera plus accessible aux visiteurs.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowUnpublishConfirm(false)} className="border-[#2A3441] text-[#94A3B8]">Annuler</Button>
              <Button data-testid="confirm-unpublish-btn" onClick={() => togglePublish("draft")} disabled={publishing} className="bg-[#F59E0B] hover:bg-[#D97706] text-black font-bold">
                {publishing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                Confirmer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL: END EVENT CONFIRMATION ===== */}
      {showEndConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="end-event-modal">
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-6 max-w-md w-full space-y-4">
            <div className="flex items-center gap-3">
              <div className="bg-[#8B5CF6]/10 p-2 rounded-full"><CheckCircle2 className="h-5 w-5 text-[#8B5CF6]" /></div>
              <h3 className="text-white font-bold text-lg">Terminer l'evenement ?</h3>
            </div>
            <p className="text-[#94A3B8] text-sm">
              L'evenement sera marque comme <strong className="text-[#8B5CF6]">termine</strong>. Les inscriptions seront automatiquement fermees. La page publique restera accessible avec le statut "Evenement termine".
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowEndConfirm(false)} className="border-[#2A3441] text-[#94A3B8]">Annuler</Button>
              <Button data-testid="confirm-end-btn" onClick={() => togglePublish("ended")} disabled={publishing} className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white font-bold">
                {publishing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                Terminer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL: CANCEL EVENT CONFIRMATION ===== */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="cancel-event-modal">
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-6 max-w-md w-full space-y-4">
            <div className="flex items-center gap-3">
              <div className="bg-red-500/10 p-2 rounded-full"><XCircle className="h-5 w-5 text-red-400" /></div>
              <h3 className="text-white font-bold text-lg">Annuler l'evenement ?</h3>
            </div>
            <p className="text-[#94A3B8] text-sm">
              L'evenement sera marque comme <strong className="text-red-400">annule</strong>. Les inscriptions seront automatiquement fermees. La page publique affichera "Evenement annule".
            </p>
            <p className="text-[#F59E0B] text-xs flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" />
              Vous pourrez republier l'evenement ulterieurement si necessaire.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowCancelConfirm(false)} className="border-[#2A3441] text-[#94A3B8]">Retour</Button>
              <Button data-testid="confirm-cancel-event-btn" onClick={() => togglePublish("cancelled")} disabled={publishing} className="bg-red-500 hover:bg-red-600 text-white font-bold">
                {publishing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                Confirmer l'annulation
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL: REMINDER CONFIRMATION ===== */}
      {showReminderConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="reminder-modal">
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-6 max-w-md w-full space-y-4">
            <div className="flex items-center gap-3">
              <div className="bg-[#0099CC]/10 p-2 rounded-full"><Mail className="h-5 w-5 text-[#0099CC]" /></div>
              <h3 className="text-white font-bold text-lg">Envoyer un rappel ?</h3>
            </div>
            <p className="text-[#94A3B8] text-sm">
              Vous etes sur le point d'envoyer un email de rappel a <strong className="text-white">{participants.filter(p => p.status === "registered" && p.email).length} participant(s)</strong> inscrits avec leur QR code d'acces.
            </p>
            {event.last_reminder_sent && (
              <p className="text-[#F59E0B] text-xs flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" />
                Dernier rappel envoye le {new Date(event.last_reminder_sent).toLocaleDateString("fr-FR", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}
              </p>
            )}
            {reminderResult && (
              <div className={`text-sm p-3 rounded-lg ${reminderResult.failed > 0 ? "bg-[#F59E0B]/10 text-[#F59E0B]" : "bg-[#10B981]/10 text-[#10B981]"}`}>
                {reminderResult.sent} envoye(s) / {reminderResult.failed} echec(s)
              </div>
            )}
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => { setShowReminderConfirm(false); setReminderResult(null); }} className="border-[#2A3441] text-[#94A3B8]">Fermer</Button>
              <Button data-testid="confirm-reminder-btn" onClick={sendReminder} disabled={reminderSending} className="bg-[#0099CC] hover:bg-[#0088B5] text-white font-bold">
                {reminderSending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
                {reminderSending ? "Envoi en cours..." : "Envoyer"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL: ADD PARTICIPANT ===== */}
      {showAddParticipant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="add-participant-modal">
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-6 max-w-md w-full space-y-4">
            <div className="flex items-center gap-3">
              <div className="bg-[#0099CC]/10 p-2 rounded-full"><UserPlus className="h-5 w-5 text-[#0099CC]" /></div>
              <h3 className="text-white font-bold text-lg">Ajouter un participant</h3>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Prenom *" value={addForm.first_name} onChange={e => setAddForm(p => ({ ...p, first_name: e.target.value }))} className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="add-first-name" />
                <Input placeholder="Nom *" value={addForm.last_name} onChange={e => setAddForm(p => ({ ...p, last_name: e.target.value }))} className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="add-last-name" />
              </div>
              <Input placeholder="Email" value={addForm.email} onChange={e => setAddForm(p => ({ ...p, email: e.target.value }))} className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="add-email" />
              <Input placeholder="Telephone (ex: +33612345678)" value={addForm.phone} onChange={e => setAddForm(p => ({ ...p, phone: e.target.value }))} className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="add-phone" />
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Pays de residence" value={addForm.country} onChange={e => setAddForm(p => ({ ...p, country: e.target.value }))} className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="add-country" />
                <Input placeholder="Ville de residence" value={addForm.city} onChange={e => setAddForm(p => ({ ...p, city: e.target.value }))} className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="add-city" />
              </div>
              {event.max_capacity > 0 && event.total_registered >= event.max_capacity && (
                <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg p-3 space-y-2">
                  <p className="text-[#F59E0B] text-xs font-medium flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Capacite atteinte ({event.total_registered}/{event.max_capacity})</p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={addForm.override_capacity} onChange={e => setAddForm(p => ({ ...p, override_capacity: e.target.checked, is_vip: e.target.checked || p.is_vip }))} className="accent-[#F59E0B]" data-testid="override-capacity-checkbox" />
                    <span className="text-white text-xs">Invitation speciale / VIP — depasser la capacite</span>
                  </label>
                  {addForm.override_capacity && (
                    <Input placeholder="Motif (facultatif)" value={addForm.override_reason} onChange={e => setAddForm(p => ({ ...p, override_reason: e.target.value }))} className="bg-[#0B0F19] border-[#2A3441] text-white text-xs" data-testid="override-reason" />
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowAddParticipant(false)} className="border-[#2A3441] text-[#94A3B8]">Annuler</Button>
              <Button data-testid="confirm-add-participant-btn" onClick={addParticipant} disabled={addingParticipant} className="bg-[#0099CC] hover:bg-[#0088B5] text-white font-bold">
                {addingParticipant ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <UserPlus className="h-4 w-4 mr-1.5" />}
                {addingParticipant ? "Ajout..." : "Ajouter"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL: PERSONALIZED INVITATIONS ===== */}
      {showInvitationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="invitation-modal">
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-6 max-w-lg w-full space-y-4">
            <div className="flex items-center gap-3">
              <div className="bg-[#10B981]/10 p-2 rounded-full"><Send className="h-5 w-5 text-[#10B981]" /></div>
              <div>
                <h3 className="text-white font-bold text-lg">Envoyer des invitations</h3>
                <p className="text-[#64748b] text-xs">{selectedParticipants.size} participant(s) selectionne(s)</p>
              </div>
            </div>

            <div className="bg-[#0B0F19] rounded-xl p-4">
              <p className="text-sm text-[#94A3B8] mb-2">Participants selectionnes :</p>
              <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                {Array.from(selectedParticipants).map(pid => {
                  const p = participants.find(x => x.id === pid);
                  return p ? (
                    <span key={pid} className="text-[10px] bg-[#1E293B] text-white px-2 py-0.5 rounded-full">
                      {p.first_name} {p.last_name}
                    </span>
                  ) : null;
                })}
              </div>
            </div>

            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block">Message personnalise (facultatif)</label>
              <textarea data-testid="invitation-message-input" value={invitationMessage} onChange={e => setInvitationMessage(e.target.value)}
                placeholder="Ajoutez un message personnel a l'invitation..."
                className="w-full rounded-lg bg-[#0B0F19] border border-[#2A3441] text-white p-3 text-sm min-h-[80px] resize-none focus:ring-1 focus:ring-[#10B981] focus:border-[#10B981]" />
            </div>

            <div className="bg-[#0B1120] border border-[#1E3A5F] rounded-lg p-3 text-xs text-[#94A3B8] space-y-1">
              <p className="font-semibold text-white">L'invitation inclura :</p>
              <ul className="space-y-0.5 ml-3">
                <li>- Details de l'evenement (titre, date, lieu, format)</li>
                {event?.event_format !== "presentiel" && <li>- Lien securise de connexion en ligne</li>}
                {event?.event_format !== "en_ligne" && <li>- QR Code d'acces personnel</li>}
                <li>- Reference d'inscription</li>
              </ul>
            </div>

            {invitationResult && (
              <div className={`p-3 rounded-lg text-sm ${invitationResult.sent > 0 ? "bg-[#10B981]/10 text-[#10B981]" : "bg-red-500/10 text-red-400"}`}>
                {invitationResult.sent} invitation(s) envoyee(s){invitationResult.failed > 0 && `, ${invitationResult.failed} echec(s)`}
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => { setShowInvitationModal(false); setInvitationResult(null); }} className="border-[#2A3441] text-[#94A3B8]">Annuler</Button>
              <Button data-testid="confirm-send-invitations" onClick={sendInvitations} disabled={invitationSending}
                className="bg-[#10B981] hover:bg-[#059669] text-white">
                {invitationSending ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Envoi...</> : <><Send className="h-4 w-4 mr-2" /> Envoyer</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
